import { useEffect, useState } from "react";
import api from "@/lib/api";
import { GlassCard, PageLoader, StatusBadge } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { RotateCcw, Search, ShieldCheck, ShieldAlert } from "lucide-react";

export default function Restore() {
  const [servers, setServers] = useState(null);
  const [sid, setSid] = useState("");
  const [snaps, setSnaps] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/servers").then((r) => {
      const ready = r.data.filter((s) => s.status === "healthy");
      setServers(ready);
      if (ready[0]) setSid(ready[0].id);
    });
  }, []);
  useEffect(() => { if (sid) api.get(`/servers/${sid}/snapshots`).then((r) => setSnaps(r.data)); }, [sid]);
  if (!servers) return <PageLoader />;

  const filtered = snaps.filter((s) => `${s.vmid} ${s.name}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <div className="mb-2 flex items-center gap-2"><RotateCcw className="h-5 w-5 text-cyan-400" /><h1 className="font-display text-2xl font-bold text-slate-50">Restore Center</h1></div>
      <p className="mb-6 text-sm text-slate-400">Backups aren't useful unless they can be restored. Find any snapshot, then restore through your standard Proxmox workflow.</p>

      {servers.length === 0 ? (
        <GlassCard className="p-8 text-center text-slate-400">No ready servers yet.</GlassCard>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap gap-3">
            <select value={sid} onChange={(e) => setSid(e.target.value)} data-testid="restore-server-select" className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200">
              {servers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search VM/CT by id or name" data-testid="restore-search" className="bg-slate-950 border-slate-700 pl-9" />
            </div>
          </div>

          <GlassCard className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-800 text-left text-xs uppercase text-slate-500">
                <tr><th className="p-3">VM/CT</th><th className="p-3">Snapshot</th><th className="p-3">Size</th><th className="p-3">Verification</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60">
                {filtered.map((s) => (
                  <tr key={s.id} className="hover:bg-slate-900/40" data-testid={`snapshot-row-${s.id}`}>
                    <td className="p-3"><span className="font-mono text-cyan-300">{s.vmid}</span> <span className="text-slate-200">{s.name}</span> <span className="ml-1 rounded bg-slate-800 px-1.5 py-0.5 text-xs text-slate-400">{s.type}</span></td>
                    <td className="p-3 text-slate-400">{new Date(s.date).toLocaleString()}</td>
                    <td className="p-3 font-mono text-slate-300">{s.size_gb} GB</td>
                    <td className="p-3">{s.verified ? <span className="inline-flex items-center gap-1 text-emerald-400"><ShieldCheck className="h-4 w-4" /> Verified</span> : <span className="inline-flex items-center gap-1 text-amber-400"><ShieldAlert className="h-4 w-4" /> Pending</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </GlassCard>
          <div className="mt-4 rounded-xl border border-slate-800 bg-slate-900/40 p-4 text-sm text-slate-400">
            <b className="text-slate-200">To restore:</b> In Proxmox, open the target datastore, select the snapshot and restore the VM/CT. If you enabled client-side encryption, you'll need your encryption key. ZettaKeep never restores automatically on your behalf.
          </div>
        </>
      )}
    </div>
  );
}
